import Divider from '@mui/material/Divider';
import styles from './Auth.module.css';
import AlertDialog from '../../components/AlertDialog';
import { useEffect, useState } from 'react';
import InviteDialog from './InviteDialog';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import UpgradeButton from '../../components/UpgradeButton';
import WarningTooltip from '../../components/WarningTooltip';
import WatchGuideLink from '../../components/WatchGuideLink/WatchGuideLink';
import {
  getPlanTier,
  getInvitedUsersLimit,
  getBasicEditorsLimit,
  getAdvancedEditorsLimit,
} from '../../config/planLimits';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import Tooltip from '@mui/material/Tooltip';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Button, Menu, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { UserStatus } from '../../utils/enums';
import { useContext } from 'react';
import { Context } from '../../AuthContext';

const InviteUsersPage = () => {
  const { user } = useContext(Context);
  const { subscriptionStatus } = useOutletContext() || {};
  const planTier = getPlanTier(subscriptionStatus);
  const invitedLimit = getInvitedUsersLimit(subscriptionStatus);
  const basicEditorsLimit = getBasicEditorsLimit(subscriptionStatus);
  const advancedEditorsLimit = getAdvancedEditorsLimit(subscriptionStatus);
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const { vertical_id } = useParams();
  const [loadingStatus, setLoadingStatus] = useState({});
  const [loadingPermission, setLoadingPermission] = useState({});
  const [permissionMenuAnchor, setPermissionMenuAnchor] = useState({ el: null, email: null });

  const openPermissionMenu = (event, email) => {
    setPermissionMenuAnchor({ el: event.currentTarget, email });
  };

  const closePermissionMenu = () => {
    setPermissionMenuAnchor({ el: null, email: null });
  };

  const handlePermissionSelect = (person, newPermission) => {
    closePermissionMenu();
    const current = person.permission || 'Member';
    if (current === newPermission) return;
    changeUserPermission(person.name, person.email, vertical_id, newPermission);
  };

  const formatPermission = (p) => {
    if (p === 'Editor') return 'Basic Editor';
    if (p === 'AdvancedEditor') return 'Advanced Editor';
    return p || 'Member';
  };

  const permissionMenuItemSx = {
    paddingY: '6px',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '24px',
    color: '#344054',
    '&.Mui-selected': {
      backgroundColor: '#DEEAFF',
      color: '#006ED4',
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#DEEAFF',
    },
  };

  const addPerson = (person) => {
    setPeople((prevPeople) => {
      const existingPerson = prevPeople.find((p) => p.email === person.email);
      if (existingPerson) {
        return prevPeople.map((p) => (p.email === person.email ? person : p));
      }
      return [...prevPeople, person];
    });
  };

  useEffect(() => {
    document.title = 'Invite Users - The Simple Platform';
    const getPeople = async () => {
      const users = await fetchPeople();
      if (!Array.isArray(users)) {
        return;
      }
      // exclude the current user from list
      setPeople(users.filter((person) => person.email !== user?.email));
    };
    getPeople();
  }, [navigate, user]);

  const invite = async (email, name, vertical_id, permission = 'Member') => {
    setLoadingStatus((prev) => ({ ...prev, [email]: true }));
    const token = await user.getIdToken();
    let url = process.env.REACT_APP_FLASK_WEBSERVER + 'inviteUser';

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        name: name,
        vertical_id: vertical_id,
        permission: permission,
      }),
    });

    const response = await res.json();
    if (res.status == 200 || res.status == 201) {
      addPerson({
        name: name,
        email: email,
        _id: response._id,
        status: 'Invited',
        permission: permission,
      });
      toast.success('Invitation sent successfully!', {
        className: 'toast',
        autoClose: 3000,
      });
    } else {
      toast.error('Unable to invite user! ' + response.message, {
        className: 'toast',
        autoClose: 3000,
      });
    }
    setLoadingStatus((prev) => ({ ...prev, [email]: false }));
  };

  const changeUserStatus = async (name, email, vertical_id, status, permission) => {
    setLoadingStatus((prev) => ({ ...prev, [email]: true }));
    const token = await user.getIdToken();
    let url =
      process.env.REACT_APP_FLASK_WEBSERVER +
      `${status === 'Active' ? 'de' : ''}activateUser?email=${email}&vertical_id=${vertical_id}`;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const response = await res.json();
    if (res.status == 200) {
      const isDeactivating = status === 'Active';
      addPerson({
        name: name,
        email: email,
        status: isDeactivating ? 'Deactivated' : 'Active',
        permission: isDeactivating ? 'Member' : permission,
      });
      toast.success(`User ${status === 'Active' ? 'de' : ''}activated successfully!`, {
        className: 'toast',
        autoClose: 3000,
      });
    } else {
      toast.error(
        `Unable to ${status === 'Active' ? 'de' : ''}activate user! ` + response.message,
        {
          className: 'toast',
          autoClose: 3000,
        }
      );
    }
    setLoadingStatus((prev) => ({ ...prev, [email]: false }));
  };

  const changeUserPermission = async (name, email, vertical_id, newPermission) => {
    setLoadingPermission((prev) => ({ ...prev, [email]: true }));
    const token = await user.getIdToken();
    let url =
      process.env.REACT_APP_FLASK_WEBSERVER +
      `changePermission?email=${email}&vertical_id=${vertical_id}&new_permission=${newPermission}`;

    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const response = await res.json();

    if (res.status == 200) {
      addPerson({
        ...people.find((p) => p.email === email),
        permission: response.new_permission,
      });
      toast.success(
        `Permission changed to ${formatPermission(response.new_permission)} successfully!`,
        {
          className: 'toast',
          autoClose: 3000,
        }
      );
    } else {
      toast.error(`Unable to change permission! ` + response.message, {
        className: 'toast',
        autoClose: 3000,
      });
    }
    setLoadingPermission((prev) => ({ ...prev, [email]: false }));
  };

  const fetchPeople = async () => {
    const token = await user.getIdToken();
    const url =
      process.env.REACT_APP_FLASK_WEBSERVER + 'getUsersAndInvitiations?vertical_id=' + vertical_id;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (res.status === 403) {
      navigate('/forbidden', { replace: true });
      return null;
    }

    const data = await res.json();
    return data;
  };

  const deleteInvitation = async (email) => {
    setPeople(
      people.map((person) =>
        person.email === email ? { ...person, status: 'Disinvited', permission: 'Member' } : person
      )
    );

    const url =
      process.env.REACT_APP_FLASK_WEBSERVER +
      'disinviteUser?vertical_id=' +
      vertical_id +
      '&email=' +
      email;
    const token = await user.getIdToken();
    await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const inactiveStatuses = [
    UserStatus.DISINVITED,
    UserStatus.DECLINED,
    UserStatus.LEFT,
    UserStatus.VERTICAL_DELETED,
    UserStatus.DEACTIVATED,
  ];
  const activePeople = people.filter((p) => !inactiveStatuses.includes(p.status));
  const invitedCount = activePeople.length;
  const basicEditorCount = activePeople.filter((p) => p.permission === 'Editor').length;
  const advancedEditorCount = activePeople.filter((p) => p.permission === 'AdvancedEditor').length;
  const inviteDisabled = invitedCount >= invitedLimit;

  return (
    <div style={{ width: '75vw' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '30px 0',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '16px',
          }}
        >
          <h1>Invited Users</h1>
          <WatchGuideLink
            videoId="inviting-collaborators"
            label="Watch Guide: Inviting Collaborators"
          />
        </div>
        <WarningTooltip
          message="You've reached your invited user limit for this Vertical. Remove an existing user or upgrade to Full Access to add more collaborators."
          disabled={!inviteDisabled}
        >
          <span>
            <InviteDialog
              disabled={inviteDisabled}
              button={
                <Button
                  variant="outlined"
                  sx={{
                    height: '40px',
                    textTransform: 'none',
                    fontWeight: 700,
                    border: '1px solid #356AC3',
                    boxShadow: '0px 1px 4px 0px #112E6912',
                    backgroundColor: 'white',
                  }}
                >
                  <AddIcon sx={{ paddingRight: '10px' }} />
                  Add New User
                </Button>
              }
              updatePeople={addPerson}
            />
          </span>
        </WarningTooltip>
      </div>

      {(() => {
        if (planTier === 'free') {
          return (
            <div style={{ textAlign: 'left', marginBottom: '20px' }}>
              <p style={{ margin: 0, fontWeight: 700 }}>
                Invited Users: {invitedCount} of {invitedLimit}
              </p>
              <p style={{ margin: 0, fontWeight: 700 }}>
                Basic Editors: {basicEditorCount} of {basicEditorsLimit}
              </p>
              <p style={{ margin: '0 0 12px 0', fontWeight: 700 }}>
                Advanced Editors: {advancedEditorCount} of {advancedEditorsLimit}
              </p>
              <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: 'gray', lineHeight: 1.4 }}>
                <strong>Basic Editors</strong> can edit content, organize resources, and add notes
                within the Vertical.
              </p>
              <p style={{ margin: '0', fontSize: '13px', color: 'gray', lineHeight: 1.4 }}>
                <strong>Advanced Editors</strong> have the same permissions, plus the ability to run
                Discovery Refreshes and invite additional collaborators.
              </p>
              <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'gray', lineHeight: 1.4 }}>
                Basic Editor and Advanced Editor permissions are only available for collaborators
                on a Full Access plan.
              </p>
              <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'gray', lineHeight: 1.4 }}>
                Upgrade to Full Access for expanded collaboration capacity and increased invited
                user limits.
              </p>
              <UpgradeButton />
            </div>
          );
        }

        return (
          <div style={{ textAlign: 'left', marginBottom: '20px' }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: 700 }}>Editor Permission Levels</p>
            <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: 'gray', lineHeight: 1.4 }}>
              <strong>Basic Editors</strong> can edit content, organize resources, and add notes
              within the Vertical.
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: 'gray', lineHeight: 1.4 }}>
              <strong>Advanced Editors</strong> have the same permissions, plus the ability to run
              Discovery Refreshes and invite additional collaborators.
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: 'gray', lineHeight: 1.4 }}>
              Basic Editor and Advanced Editor permissions are only available for collaborators on
              a Full Access plan.
            </p>
          </div>
        );
      })()}

      <div style={{ display: 'flex', justifyContent: 'space-between', textAlign: 'left' }}>
        <span className={styles['usersTableLabel']}>Full Name</span>
        <span className={styles['usersTableLabel']}>Email</span>
        <span className={styles['usersTableLabel']}>Status</span>
        {/* <span className={styles['usersTableLabel']}>Eligible to be an Editor?</span> */}
        <span className={styles['usersTableLabel']}>Permission</span>
        <span style={{ width: '10%' }}></span>
      </div>
      <Divider sx={{ my: 0.5 }} />
      <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
        {people.map((person, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              backgroundColor: i % 2 === 1 ? '#EBF0F9' : '',
            }}
          >
            <span className={styles['users']}>{person.name}</span>
            <span className={styles['users']}>{person.email}</span>
            <span className={styles['users']}>
              {person.status}{' '}
              {!loadingStatus[person.email] ? (
                <Tooltip
                  arrow
                  title={
                    person.status === 'Active'
                      ? 'Deactivate'
                      : person.status === 'Deactivated'
                      ? 'Activate'
                      : 'Resend invitation'
                  }
                  placement="top"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -10],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <div className={styles['controlUsers']}>
                    {[
                      UserStatus.INVITED,
                      UserStatus.DISINVITED,
                      UserStatus.DECLINED,
                      UserStatus.LEFT,
                    ].includes(person.status) ? (
                      <RestartAltOutlinedIcon
                        onClick={() =>
                          invite(person.email, person.name, vertical_id, person.permission)
                        }
                      />
                    ) : (
                      <KeyboardArrowDownOutlinedIcon
                        onClick={() =>
                          changeUserStatus(
                            person.name,
                            person.email,
                            vertical_id,
                            person.status,
                            person.permission
                          )
                        }
                      />
                    )}
                  </div>
                </Tooltip>
              ) : (
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  style={{ marginLeft: '10px' }}
                ></FontAwesomeIcon>
              )}
            </span>
            <span className={styles['users']}>
              {formatPermission(person.permission)}{' '}
              <>
                {!loadingPermission[person.email] ? (
                  <div className={styles['controlUsers']}>
                    <KeyboardArrowDownOutlinedIcon
                      onClick={(e) => openPermissionMenu(e, person.email)}
                    />
                    <Menu
                      anchorEl={permissionMenuAnchor.el}
                      open={permissionMenuAnchor.email === person.email}
                      onClose={closePermissionMenu}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      slotProps={{ paper: { sx: { boxShadow: '0px 2px 8px 0px #1018281A' } } }}
                    >
                      <MenuItem
                        selected={(person.permission || 'Member') === 'Member'}
                        onClick={() => handlePermissionSelect(person, 'Member')}
                        sx={permissionMenuItemSx}
                      >
                        Member
                      </MenuItem>
                      {(() => {
                        const inviteeIneligible =
                          getPlanTier(person.subscription_status) !== 'paid';
                        const basicCapHit = basicEditorCount >= basicEditorsLimit;
                        const basicDisabled =
                          person.permission !== 'Editor' && (basicCapHit || inviteeIneligible);
                        const inviterCapHit = advancedEditorCount >= advancedEditorsLimit;
                        const advancedDisabled =
                          person.permission !== 'AdvancedEditor' &&
                          (inviterCapHit || inviteeIneligible);
                        const advancedTooltipMessage = inviterCapHit
                          ? 'Your Explorer account cannot assign Advanced Editors. Upgrade to enable this permission.'
                          : 'This user is on an Explorer account and cannot be assigned as an Advanced Editor.';
                        const basicTooltipMessage = basicCapHit
                          ? planTier === 'free'
                            ? 'Your Explorer account cannot assign Basic Editors. Upgrade to enable this permission.'
                            : `Basic Editor limit reached (${basicEditorCount} of ${basicEditorsLimit}).`
                          : 'This user is on an Explorer account and cannot be assigned as a Basic Editor.';
                        return (
                          <>
                            <WarningTooltip
                              message={basicTooltipMessage}
                              disabled={!basicDisabled}
                            >
                              <span>
                                <MenuItem
                                  selected={person.permission === 'Editor'}
                                  disabled={basicDisabled}
                                  onClick={() => handlePermissionSelect(person, 'Editor')}
                                  sx={permissionMenuItemSx}
                                >
                                  Basic Editor
                                </MenuItem>
                              </span>
                            </WarningTooltip>
                            <WarningTooltip
                              message={advancedTooltipMessage}
                              disabled={!advancedDisabled}
                            >
                              <span>
                                <MenuItem
                                  selected={person.permission === 'AdvancedEditor'}
                                  disabled={advancedDisabled}
                                  onClick={() => handlePermissionSelect(person, 'AdvancedEditor')}
                                  sx={permissionMenuItemSx}
                                >
                                  Advanced Editor
                                </MenuItem>
                              </span>
                            </WarningTooltip>
                          </>
                        );
                      })()}
                    </Menu>
                  </div>
                ) : (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    style={{ marginLeft: '10px' }}
                  ></FontAwesomeIcon>
                )}
              </>
            </span>
            <span
              style={{
                width: '10%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '20px',
              }}
            >
              <>
                {[
                  UserStatus.INVITED,
                  UserStatus.DECLINED,
                  UserStatus.DISINVITED,
                  UserStatus.LEFT,
                ].includes(person.status) && (
                  <InviteDialog
                    button={
                      <img
                        src={`${process.env.PUBLIC_URL}/edit.svg`}
                        alt="edit"
                        style={{ cursor: 'pointer' }}
                      />
                    }
                    updatePeople={addPerson}
                    functionality="edit"
                    name={person.name}
                    email={person.email}
                    permission={person.permission || 'Member'}
                  />
                )}
                {[UserStatus.INVITED].includes(person.status) && (
                  <AlertDialog
                    id={person._id}
                    action={() => deleteInvitation(person.email)}
                    dialogTitle={'Delete Invitation'}
                    dialogContentText={
                      'This will delete the invitation and the user will no longer be able to join the vertical.'
                    }
                  />
                )}
              </>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
export default InviteUsersPage;
